import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Building2, Save, Mail, Phone, MapPin, User, Image, Upload, Trash2, Loader2, 
  FileImage, Handshake, CheckCircle, XCircle, Percent, Euro, Calendar,
  AlertTriangle, Download, Clock, RefreshCw, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOTIVI_CANCELLAZIONE = [
  { value: "prezzo_troppo_alto", label: "Il prezzo è troppo alto" },
  { value: "non_uso_piu", label: "Non uso più il servizio" },
  { value: "altro_software", label: "Ho trovato un altro software" },
  { value: "funzionalita_mancanti", label: "Mancano funzionalità di cui ho bisogno" },
  { value: "difficolta_uso", label: "È troppo difficile da usare" },
  { value: "chiusura_associazione", label: "L'associazione sta chiudendo" },
  { value: "altro", label: "Altro motivo" }
];

export default function ImpostazioniEnte() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  // Convenzione state
  const [convenzione, setConvenzione] = useState(null);
  const [codiceConvenzione, setCodiceConvenzione] = useState('');
  const [loadingConvenzione, setLoadingConvenzione] = useState(false);
  const [submittingConvenzione, setSubmittingConvenzione] = useState(false);
  
  // Cancellazione Account state
  const [statoCancellazione, setStatoCancellazione] = useState(null);
  const [loadingCancellazione, setLoadingCancellazione] = useState(false);
  const [cancelStep, setCancelStep] = useState(0); // 0=none, 1=first, 2=second, 3=final
  const [motivoCancellazione, setMotivoCancellazione] = useState('');
  const [motivoDettaglio, setMotivoDettaglio] = useState('');
  const [confermaBackup, setConfermaBackup] = useState(false);
  const [submittingCancellazione, setSubmittingCancellazione] = useState(false);
  
  // Polizza Collettiva Volontari state
  const [polizzaData, setPolizzaData] = useState({
    polizza_volontari_collettiva: '',
    polizza_volontari_compagnia: '',
    polizza_volontari_decorrenza: '',
    polizza_volontari_scadenza: '',
    polizza_volontari_tipologia: 'Infortuni Multirischi Volontariato'
  });
  const [savingPolizza, setSavingPolizza] = useState(false);
  const [showPolizzaConfirmDialog, setShowPolizzaConfirmDialog] = useState(false);
  const [polizzaSyncReport, setPolizzaSyncReport] = useState(null);
  
  const [formData, setFormData] = useState({
    denominazione: '',
    codice_fiscale: '',
    partita_iva: '',
    natura_giuridica: '',
    indirizzo: '',
    regione: '',
    email: '',
    pec: '',
    telefono: '',
    referente_legale: '',
    referente_volontari: '',
    note: '',
    logo_base64: null,
    // Registri e Iscrizioni
    registro_volontariato_regione: '',
    registro_volontariato_data: '',
    registro_volontariato_numero: '',
    registro_protezione_civile_regione: '',
    registro_protezione_civile_data: '',
    registro_protezione_civile_numero: '',
    runts_data: '',
    runts_ruolo: '',
    runts_numero: ''
  });

  useEffect(() => {
    fetchEnte();
    fetchConvenzione();
    fetchStatoCancellazione();
    fetchPolizzaCollettiva();
  }, []);

  const fetchPolizzaCollettiva = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ente/polizza-volontari`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPolizzaData({
          polizza_volontari_collettiva: data.polizza_volontari_collettiva || '',
          polizza_volontari_compagnia: data.polizza_volontari_compagnia || '',
          polizza_volontari_decorrenza: data.polizza_volontari_decorrenza || '',
          polizza_volontari_scadenza: data.polizza_volontari_scadenza || '',
          polizza_volontari_tipologia: data.polizza_volontari_tipologia || 'Infortuni Multirischi Volontariato'
        });
      }
    } catch (error) {
      console.error('Errore fetch polizza collettiva:', error);
    }
  };

  const handleSavePolizzaCollettiva = async () => {
    setSavingPolizza(true);
    setPolizzaSyncReport(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ente/polizza-volontari`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(polizzaData)
      });
      const data = await response.json();
      if (response.ok) {
        setPolizzaSyncReport(data);
        toast.success(`Polizza collettiva salvata! ${data.aggiornati || 0} coperture aggiornate, ${data.creati || 0} create`, { duration: 6000 });
        setShowPolizzaConfirmDialog(false);
      } else {
        toast.error(data.detail || 'Errore nel salvataggio della polizza');
      }
    } catch (error) {
      console.error('Errore salvataggio polizza:', error);
      toast.error('Errore durante il salvataggio');
    } finally {
      setSavingPolizza(false);
    }
  };

  const fetchEnte = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ente`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          ...data
        }));
      }
    } catch (error) {
      console.error('Error fetching ente:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConvenzione = async () => {
    setLoadingConvenzione(true);
    try {
      const response = await fetch(`${API_URL}/api/ente/convenzione`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setConvenzione(data);
      }
    } catch (error) {
      console.error('Error fetching convenzione:', error);
    } finally {
      setLoadingConvenzione(false);
    }
  };

  // ============= CANCELLAZIONE ACCOUNT FUNCTIONS =============
  
  const fetchStatoCancellazione = async () => {
    setLoadingCancellazione(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ente/stato-cancellazione`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStatoCancellazione(data);
      }
    } catch (error) {
      console.error('Error fetching stato cancellazione:', error);
    } finally {
      setLoadingCancellazione(false);
    }
  };

  const handleRichiediCancellazione = async () => {
    if (!motivoCancellazione) {
      toast.error('Seleziona un motivo per la cancellazione');
      return;
    }
    if (!confermaBackup) {
      toast.error('Devi confermare di aver scaricato il backup o di non averne bisogno');
      return;
    }
    
    setSubmittingCancellazione(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ente/richiedi-cancellazione`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          motivo: motivoCancellazione,
          motivo_dettaglio: motivoDettaglio,
          conferma_backup: confermaBackup
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setCancelStep(0);
        setMotivoCancellazione('');
        setMotivoDettaglio('');
        setConfermaBackup(false);
        fetchStatoCancellazione();
      } else {
        toast.error(data.detail || 'Errore nella richiesta');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSubmittingCancellazione(false);
    }
  };

  const handleAnnullaCancellazione = async () => {
    setSubmittingCancellazione(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ente/annulla-cancellazione`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        fetchStatoCancellazione();
      } else {
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSubmittingCancellazione(false);
    }
  };

  const handleDownloadBackup = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/backup/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `backup_${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Backup scaricato con successo');
      } else {
        toast.error('Errore nel download del backup');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleAderisciConvenzione = async () => {
    if (!codiceConvenzione.trim()) {
      toast.error('Inserisci il codice convenzione');
      return;
    }
    
    setSubmittingConvenzione(true);
    try {
      const response = await fetch(`${API_URL}/api/ente/convenzione/aderisci`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ codice: codiceConvenzione })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setCodiceConvenzione('');
        fetchConvenzione();
      } else {
        toast.error(data.detail || 'Errore nell\'adesione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSubmittingConvenzione(false);
    }
  };

  const handleRimuoviConvenzione = async () => {
    if (!window.confirm('Sei sicuro di voler rimuovere la tua adesione alla convenzione? Tornerai al listino standard.')) {
      return;
    }
    
    setSubmittingConvenzione(true);
    try {
      const response = await fetch(`${API_URL}/api/ente/convenzione`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        fetchConvenzione();
      } else {
        toast.error(data.detail || 'Errore nella rimozione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSubmittingConvenzione(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.denominazione) {
      toast.error('La denominazione è obbligatoria');
      return;
    }

    setSaving(true);
    try {
      // Don't send logo_base64 in the regular update (it's handled separately)
      const { logo_base64, ...dataToSend } = formData;
      
      const response = await fetch(`${API_URL}/api/ente`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        const timestamp = new Date().toLocaleString('it-IT');
        toast.success(`Dati dell'ente aggiornati il ${timestamp}`);
      } else {
        toast.error('Errore durante il salvataggio');
      }
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Il file deve essere un\'immagine (PNG, JPG)');
      return;
    }

    // Validate file size (500KB)
    if (file.size > 500 * 1024) {
      toast.error('Il logo deve essere inferiore a 500KB');
      return;
    }

    setUploadingLogo(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result;
        
        try {
          const response = await fetch(`${API_URL}/api/ente/logo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ logo_base64: base64 })
          });

          if (response.ok) {
            const data = await response.json();
            setFormData(prev => ({ ...prev, logo_base64: base64 }));
            toast.success(`Logo caricato (${data.size_kb}KB)`);
          } else {
            const err = await response.json();
            toast.error(err.detail || 'Errore durante l\'upload');
          }
        } catch (error) {
          toast.error('Errore durante l\'upload del logo');
        } finally {
          setUploadingLogo(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Errore durante la lettura del file');
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Sei sicuro di voler rimuovere il logo?')) return;

    setUploadingLogo(true);
    try {
      const response = await fetch(`${API_URL}/api/ente/logo`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setFormData(prev => ({ ...prev, logo_base64: null }));
        toast.success('Logo rimosso');
      } else {
        toast.error('Errore durante la rimozione');
      }
    } catch (error) {
      toast.error('Errore durante la rimozione del logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055BB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="impostazioni-ente-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Impostazioni Ente</h1>
        <p className="page-subtitle">Gestisci i dati anagrafici dell'associazione</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Logo Section */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileImage className="w-5 h-5 text-[#0055BB]" />
              Logo Ente
            </CardTitle>
            <CardDescription>
              Carica il logo per personalizzare i documenti PDF generati
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Logo Preview */}
            <div className="flex justify-center">
              {formData.logo_base64 ? (
                <div className="relative">
                  <img 
                    src={formData.logo_base64} 
                    alt="Logo ente" 
                    className="max-w-[200px] max-h-[120px] object-contain border rounded-lg p-2"
                    data-testid="logo-preview"
                  />
                </div>
              ) : (
                <div className="w-48 h-28 bg-slate-100 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-300">
                  <div className="text-center text-slate-400">
                    <Image className="w-8 h-8 mx-auto mb-1" />
                    <p className="text-xs">Nessun logo</p>
                  </div>
                </div>
              )}
            </div>

            {/* Upload Button */}
            <div className="space-y-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/png,image/jpeg,image/jpg"
                className="hidden"
                data-testid="logo-input"
              />
              
              {formData.logo_base64 ? (
                <div className="flex gap-2">
                  <Button 
                    type="button"
                    variant="outline" 
                    className="flex-1"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    {uploadingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Cambia
                  </Button>
                  <Button 
                    type="button"
                    variant="destructive" 
                    onClick={handleLogoDelete}
                    disabled={uploadingLogo}
                    data-testid="delete-logo-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button 
                  type="button"
                  variant="outline" 
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  data-testid="upload-logo-btn"
                >
                  {uploadingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Carica Logo
                </Button>
              )}
            </div>

            <p className="text-xs text-slate-500 text-center">
              Formati: PNG, JPG • Max 500KB
            </p>
            
            <div className="bg-blue-50 p-3 rounded-lg text-xs text-blue-700">
              <p className="font-medium mb-1">Il logo verrà inserito in:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Registro Volontari PDF</li>
                <li>Certificati Attività</li>
                <li>Report Rendicontazione</li>
                <li>Kit Amministrativo</li>
                <li>Report Assicurativo</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Form Section */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              {/* Dati Identificativi */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="w-5 h-5 text-[#0055BB]" />
                    Dati Identificativi
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="denominazione">Denominazione *</Label>
                    <Input
                      id="denominazione"
                      value={formData.denominazione}
                      onChange={(e) => handleChange('denominazione', e.target.value)}
                      placeholder="Nome dell'associazione"
                      required
                      data-testid="input-denominazione"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
                    <Input
                      id="codice_fiscale"
                      value={formData.codice_fiscale}
                      onChange={(e) => handleChange('codice_fiscale', e.target.value.toUpperCase())}
                      className="font-mono uppercase"
                      data-testid="input-cf"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="partita_iva">Partita IVA</Label>
                    <Input
                      id="partita_iva"
                      value={formData.partita_iva}
                      onChange={(e) => handleChange('partita_iva', e.target.value)}
                      className="font-mono"
                      data-testid="input-piva"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="natura_giuridica">Natura Giuridica</Label>
                    <Input
                      id="natura_giuridica"
                      value={formData.natura_giuridica}
                      onChange={(e) => handleChange('natura_giuridica', e.target.value)}
                      placeholder="Es: APS, ODV, Fondazione, ETS"
                      data-testid="input-natura"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="regione">Regione</Label>
                    <Input
                      id="regione"
                      value={formData.regione || ''}
                      onChange={(e) => handleChange('regione', e.target.value)}
                      placeholder="Es: Lombardia, Lazio..."
                      data-testid="input-regione"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Sede e Contatti */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MapPin className="w-5 h-5 text-[#0055BB]" />
                    Sede e Contatti
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="indirizzo">Indirizzo Sede</Label>
                    <Input
                      id="indirizzo"
                      value={formData.indirizzo}
                      onChange={(e) => handleChange('indirizzo', e.target.value)}
                      placeholder="Via, numero civico, CAP, città"
                      data-testid="input-indirizzo"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      data-testid="input-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pec">PEC</Label>
                    <Input
                      id="pec"
                      type="email"
                      value={formData.pec}
                      onChange={(e) => handleChange('pec', e.target.value)}
                      data-testid="input-pec"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telefono">Telefono</Label>
                    <Input
                      id="telefono"
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => handleChange('telefono', e.target.value)}
                      data-testid="input-telefono"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Referenti */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="w-5 h-5 text-[#0055BB]" />
                    Referenti
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="referente_legale">Referente Legale</Label>
                    <Input
                      id="referente_legale"
                      value={formData.referente_legale}
                      onChange={(e) => handleChange('referente_legale', e.target.value)}
                      placeholder="Nome del rappresentante legale"
                      data-testid="input-ref-legale"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="referente_volontari">Referente Volontari</Label>
                    <Input
                      id="referente_volontari"
                      value={formData.referente_volontari}
                      onChange={(e) => handleChange('referente_volontari', e.target.value)}
                      placeholder="Nome del coordinatore volontari"
                      data-testid="input-ref-volontari"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Note */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Note</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={formData.note}
                    onChange={(e) => handleChange('note', e.target.value)}
                    placeholder="Note aggiuntive sull'ente..."
                    rows={4}
                    data-testid="input-note"
                  />
                </CardContent>
              </Card>

              {/* Polizza Collettiva Volontari - sincronizzazione automatica */}
              <Card className="border-blue-200" data-testid="card-polizza-collettiva">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Shield className="h-5 w-5 text-blue-600" />
                    Polizza Collettiva Volontari
                  </CardTitle>
                  <CardDescription>
                    Inserisci UNA sola volta il numero della polizza &quot;Infortuni Multirischi Volontariato&quot;.
                    Verrà automaticamente sincronizzato su TUTTI i volontari dell&apos;ente (esistenti e futuri).
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-blue-700" />
                    <div>
                      <p className="font-semibold mb-1">Come funziona la sincronizzazione:</p>
                      <ul className="list-disc pl-4 space-y-0.5">
                        <li>Al salvataggio, il numero polizza viene propagato a tutti i volontari attivi</li>
                        <li>Per ogni volontario viene creata/aggiornata la copertura assicurativa</li>
                        <li>Ogni NUOVO volontario avrà automaticamente questa polizza</li>
                        <li>Solo l&apos;amministratore dell&apos;ente può modificare questo dato</li>
                      </ul>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label htmlFor="polizza_numero">Numero Polizza *</Label>
                      <Input
                        id="polizza_numero"
                        value={polizzaData.polizza_volontari_collettiva}
                        onChange={(e) => setPolizzaData({...polizzaData, polizza_volontari_collettiva: e.target.value})}
                        placeholder="Es: 123456789"
                        data-testid="input-polizza-numero"
                      />
                    </div>
                    <div>
                      <Label htmlFor="polizza_compagnia">Compagnia Assicurativa</Label>
                      <Input
                        id="polizza_compagnia"
                        value={polizzaData.polizza_volontari_compagnia}
                        onChange={(e) => setPolizzaData({...polizzaData, polizza_volontari_compagnia: e.target.value})}
                        placeholder="Es: Generali, UnipolSai"
                        data-testid="input-polizza-compagnia"
                      />
                    </div>
                    <div>
                      <Label htmlFor="polizza_decorrenza">Data Decorrenza</Label>
                      <Input
                        id="polizza_decorrenza"
                        type="date"
                        value={polizzaData.polizza_volontari_decorrenza}
                        onChange={(e) => setPolizzaData({...polizzaData, polizza_volontari_decorrenza: e.target.value})}
                        data-testid="input-polizza-decorrenza"
                      />
                    </div>
                    <div>
                      <Label htmlFor="polizza_scadenza">Data Scadenza</Label>
                      <Input
                        id="polizza_scadenza"
                        type="date"
                        value={polizzaData.polizza_volontari_scadenza}
                        onChange={(e) => setPolizzaData({...polizzaData, polizza_volontari_scadenza: e.target.value})}
                        data-testid="input-polizza-scadenza"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="polizza_tipologia">Tipologia Polizza</Label>
                      <Input
                        id="polizza_tipologia"
                        value={polizzaData.polizza_volontari_tipologia}
                        onChange={(e) => setPolizzaData({...polizzaData, polizza_volontari_tipologia: e.target.value})}
                        placeholder="Infortuni Multirischi Volontariato"
                        data-testid="input-polizza-tipologia"
                      />
                    </div>
                  </div>

                  {polizzaSyncReport && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-900">
                      <div className="flex items-center gap-2 font-semibold mb-1">
                        <CheckCircle className="h-4 w-4" />
                        Ultima sincronizzazione
                      </div>
                      <div className="text-xs">
                        Su {polizzaSyncReport.totale_volontari || 0} volontari attivi:
                        {' '}<strong>{polizzaSyncReport.aggiornati || 0}</strong> coperture aggiornate,
                        {' '}<strong>{polizzaSyncReport.creati || 0}</strong> create ex-novo
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={() => {
                        if (!polizzaData.polizza_volontari_collettiva) {
                          toast.warning('Inserisci almeno il numero polizza');
                          return;
                        }
                        setShowPolizzaConfirmDialog(true);
                      }}
                      disabled={savingPolizza}
                      data-testid="btn-save-polizza"
                    >
                      {savingPolizza ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sincronizzando...</>
                      ) : (
                        <><Shield className="h-4 w-4 mr-2" />Salva e sincronizza sui Volontari</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Dialog conferma sincronizzazione polizza */}
              <AlertDialog open={showPolizzaConfirmDialog} onOpenChange={setShowPolizzaConfirmDialog}>
                <AlertDialogContent data-testid="polizza-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-blue-600" />
                      Conferma sincronizzazione polizza
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-2 text-sm">
                        <p>
                          Stai per salvare la polizza <strong>{polizzaData.polizza_volontari_collettiva}</strong>
                          {polizzaData.polizza_volontari_compagnia ? ` (${polizzaData.polizza_volontari_compagnia})` : ''}.
                        </p>
                        <p className="text-slate-700">
                          Questa operazione <strong>propagherà il numero polizza a tutti i volontari attivi</strong>{' '}
                          dell&apos;ente, aggiornando le coperture esistenti o creandone di nuove.
                        </p>
                        <p className="text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                          ⚠️ Le coperture individuali dei volontari verranno sovrascritte con questi dati.
                        </p>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="polizza-cancel-btn">Annulla</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleSavePolizzaCollettiva}
                      data-testid="polizza-confirm-btn"
                    >
                      Sì, sincronizza ora
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Registri e Iscrizioni */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Registri e Iscrizioni</CardTitle>
                  <CardDescription>Dati di iscrizione ai registri ufficiali</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Registro Generale del Volontariato */}
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-slate-700">Iscrizione Registro Generale del Volontariato</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="reg_vol_regione" className="text-xs">Regione</Label>
                        <Input id="reg_vol_regione" value={formData.registro_volontariato_regione}
                          onChange={(e) => handleChange('registro_volontariato_regione', e.target.value)}
                          placeholder="Es: Sardegna" data-testid="input-reg-vol-regione" />
                      </div>
                      <div>
                        <Label htmlFor="reg_vol_data" className="text-xs">Data Iscrizione</Label>
                        <Input id="reg_vol_data" type="date" value={formData.registro_volontariato_data}
                          onChange={(e) => handleChange('registro_volontariato_data', e.target.value)}
                          data-testid="input-reg-vol-data" />
                      </div>
                      <div>
                        <Label htmlFor="reg_vol_numero" className="text-xs">Numero</Label>
                        <Input id="reg_vol_numero" value={formData.registro_volontariato_numero}
                          onChange={(e) => handleChange('registro_volontariato_numero', e.target.value)}
                          placeholder="N. iscrizione" data-testid="input-reg-vol-numero" />
                      </div>
                    </div>
                  </div>

                  {/* Registro Regionale Protezione Civile */}
                  <div className="space-y-3 pt-4 border-t">
                    <p className="text-sm font-semibold text-slate-700">Iscrizione Registro Regionale Protezione Civile</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="reg_pc_regione" className="text-xs">Regione</Label>
                        <Input id="reg_pc_regione" value={formData.registro_protezione_civile_regione}
                          onChange={(e) => handleChange('registro_protezione_civile_regione', e.target.value)}
                          placeholder="Es: Sardegna" data-testid="input-reg-pc-regione" />
                      </div>
                      <div>
                        <Label htmlFor="reg_pc_data" className="text-xs">Data Iscrizione</Label>
                        <Input id="reg_pc_data" type="date" value={formData.registro_protezione_civile_data}
                          onChange={(e) => handleChange('registro_protezione_civile_data', e.target.value)}
                          data-testid="input-reg-pc-data" />
                      </div>
                      <div>
                        <Label htmlFor="reg_pc_numero" className="text-xs">Numero</Label>
                        <Input id="reg_pc_numero" value={formData.registro_protezione_civile_numero}
                          onChange={(e) => handleChange('registro_protezione_civile_numero', e.target.value)}
                          placeholder="N. iscrizione" data-testid="input-reg-pc-numero" />
                      </div>
                    </div>
                  </div>

                  {/* Iscrizione RUNTS */}
                  <div className="space-y-3 pt-4 border-t">
                    <p className="text-sm font-semibold text-slate-700">Iscrizione RUNTS</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="runts_data" className="text-xs">Data Iscrizione</Label>
                        <Input id="runts_data" type="date" value={formData.runts_data}
                          onChange={(e) => handleChange('runts_data', e.target.value)}
                          data-testid="input-runts-data" />
                      </div>
                      <div>
                        <Label htmlFor="runts_ruolo" className="text-xs">Ruolo (Sezione)</Label>
                        <Input id="runts_ruolo" value={formData.runts_ruolo}
                          onChange={(e) => handleChange('runts_ruolo', e.target.value)}
                          placeholder="Es: ODV, APS, ETS" data-testid="input-runts-ruolo" />
                      </div>
                      <div>
                        <Label htmlFor="runts_numero" className="text-xs">Numero Repertorio</Label>
                        <Input id="runts_numero" value={formData.runts_numero}
                          onChange={(e) => handleChange('runts_numero', e.target.value)}
                          placeholder="N. repertorio" data-testid="input-runts-numero" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex justify-end">
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Salva Modifiche
                </Button>
              </div>
            </div>
          </form>
          
          {/* ============= SEZIONE CONVENZIONE ============= */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Handshake className="w-5 h-5 text-[#0055BB]" />
                Convenzione
              </CardTitle>
              <CardDescription>
                Aderisci a una convenzione nazionale per ottenere condizioni agevolate
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingConvenzione ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : convenzione?.has_convenzione ? (
                /* Convenzione attiva */
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                        <div>
                          <h3 className="font-semibold text-green-800">Convenzione Attiva</h3>
                          <p className="text-lg font-bold text-green-900">{convenzione.convenzione?.nome}</p>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800">Attiva</Badge>
                    </div>
                    
                    {convenzione.convenzione?.descrizione && (
                      <p className="text-sm text-green-700 mt-3">{convenzione.convenzione.descrizione}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-4 mt-4 text-sm">
                      {convenzione.convenzione?.tipo_sconto === 'percentuale' && convenzione.convenzione?.sconto_percentuale && (
                        <span className="flex items-center gap-1 text-green-700 bg-green-100 px-3 py-1 rounded-full">
                          <Percent className="w-4 h-4" />
                          Sconto {convenzione.convenzione.sconto_percentuale}%
                        </span>
                      )}
                      {convenzione.convenzione?.tipo_sconto === 'fisso' && convenzione.convenzione?.prezzo_fisso && (
                        <span className="flex items-center gap-1 text-green-700 bg-green-100 px-3 py-1 rounded-full">
                          <Euro className="w-4 h-4" />
                          Prezzo fisso €{convenzione.convenzione.prezzo_fisso}/anno
                        </span>
                      )}
                      {convenzione.convenzione?.data_scadenza && (
                        <span className="flex items-center gap-1 text-green-700">
                          <Calendar className="w-4 h-4" />
                          Valida fino al: {convenzione.convenzione.data_scadenza}
                        </span>
                      )}
                    </div>
                    
                    {convenzione.data_adesione && (
                      <p className="text-xs text-green-600 mt-3">
                        Adesione dal: {new Date(convenzione.data_adesione).toLocaleDateString('it-IT')}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={handleRimuoviConvenzione}
                      disabled={submittingConvenzione}
                    >
                      {submittingConvenzione ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <XCircle className="w-4 h-4 mr-2" />
                      )}
                      Rimuovi Convenzione
                    </Button>
                  </div>
                </div>
              ) : (
                /* Nessuna convenzione - form per inserire codice */
                <div className="space-y-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-600">
                      Se la tua associazione fa parte di una federazione nazionale (es. ANPAS, ANFFAS, Croce Rossa, ecc.),
                      potresti avere diritto a condizioni agevolate. Inserisci il codice convenzione ricevuto dalla federazione.
                    </p>
                  </div>
                  
                  <div className="flex gap-3 items-end">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="codice-convenzione">Codice Convenzione</Label>
                      <Input
                        id="codice-convenzione"
                        value={codiceConvenzione}
                        onChange={(e) => setCodiceConvenzione(e.target.value.toUpperCase())}
                        placeholder="Es. ANPAS-2026-XK9F"
                        className="font-mono text-lg"
                        data-testid="input-codice-convenzione"
                      />
                    </div>
                    <Button
                      onClick={handleAderisciConvenzione}
                      disabled={submittingConvenzione || !codiceConvenzione.trim()}
                      className="bg-[#0055BB] hover:bg-[#003d88]"
                      data-testid="btn-aderisci-convenzione"
                    >
                      {submittingConvenzione ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifica...
                        </>
                      ) : (
                        <>
                          <Handshake className="w-4 h-4 mr-2" />
                          Aderisci
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <p className="text-xs text-gray-500">
                    Non hai un codice? Contatta la tua federazione nazionale o l'assistenza AssieticaGest.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* ============= SEZIONE CANCELLAZIONE ACCOUNT ============= */}
          <Card className="mt-6 border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Cancellazione Account
              </CardTitle>
              <CardDescription>
                Richiedi la chiusura definitiva del tuo account e la cancellazione dei dati
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingCancellazione ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : statoCancellazione?.in_cancellazione ? (
                /* Account in fase di cancellazione */
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Clock className="w-6 h-6 text-amber-600 mt-1" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-amber-800">Cancellazione in corso</h3>
                        <p className="text-amber-700 mt-1">
                          La tua richiesta di cancellazione è stata registrata.
                        </p>
                        <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-amber-600">Data cessazione:</span>
                            <span className="font-bold ml-2 text-amber-800">
                              {statoCancellazione.data_cessazione_prevista
                                ? new Date(statoCancellazione.data_cessazione_prevista).toLocaleDateString('it-IT')
                                : 'N/D'}
                            </span>
                          </div>
                          <div>
                            <span className="text-amber-600">Giorni rimanenti:</span>
                            <span className="font-bold ml-2 text-amber-800">
                              {statoCancellazione.giorni_rimanenti || 0}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-amber-600 mt-3">
                          Puoi ancora annullare la cancellazione finché l'account è attivo.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <Button 
                      variant="outline" 
                      onClick={handleDownloadBackup}
                      data-testid="btn-download-backup-pending"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Scarica Backup
                    </Button>
                    
                    <Button
                      variant="outline"
                      className="border-green-500 text-green-700 hover:bg-green-50"
                      onClick={handleAnnullaCancellazione}
                      disabled={submittingCancellazione}
                      data-testid="btn-annulla-cancellazione"
                    >
                      {submittingCancellazione ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Annulla Cancellazione
                    </Button>
                  </div>
                </div>
              ) : statoCancellazione?.cessato ? (
                /* Account già cessato */
                <div className="bg-red-50 border border-red-300 rounded-lg p-4 text-center">
                  <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-red-800">Account Cessato</h3>
                  <p className="text-red-600 text-sm mt-1">
                    Questo account è stato chiuso. Contatta l'assistenza per informazioni.
                  </p>
                </div>
              ) : (
                /* Account attivo - possibilità di richiedere cancellazione */
                <div className="space-y-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-700 text-sm">
                      Se desideri chiudere il tuo account, potrai richiedere la cancellazione. 
                      Avrai <strong>30 giorni</strong> per ripensarci prima che l'account venga disattivato definitivamente.
                    </p>
                    <ul className="mt-3 text-sm text-gray-600 space-y-1">
                      <li>• Ti consigliamo di scaricare un backup dei tuoi dati prima di procedere</li>
                      <li>• Dopo 24 mesi dalla cessazione, i dati verranno anonimizzati (GDPR)</li>
                      <li>• Potrai annullare la cancellazione entro il periodo di grazia</li>
                    </ul>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <Button 
                      variant="outline" 
                      onClick={handleDownloadBackup}
                      data-testid="btn-download-backup"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Scarica Backup Dati
                    </Button>
                    
                    <AlertDialog open={cancelStep > 0} onOpenChange={(open) => !open && setCancelStep(0)}>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="destructive"
                          onClick={() => setCancelStep(1)}
                          data-testid="btn-inizia-cancellazione"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Richiedi Cancellazione
                        </Button>
                      </AlertDialogTrigger>
                      
                      <AlertDialogContent className="max-w-lg">
                        {/* Step 1: Conferma intenzione */}
                        {cancelStep === 1 && (
                          <>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="flex items-center gap-2 text-red-700">
                                <AlertTriangle className="w-5 h-5" />
                                Sei sicuro di voler cancellare l'account?
                              </AlertDialogTitle>
                              <AlertDialogDescription asChild>
                                <div className="space-y-3 text-left">
                                  <p>Questa azione avvierà il processo di cancellazione del tuo account.</p>
                                  <div className="bg-amber-50 p-3 rounded-lg text-amber-800 text-sm">
                                    <strong>Cosa succederà:</strong>
                                    <ul className="mt-2 space-y-1">
                                      <li>• L'account rimarrà attivo per 30 giorni</li>
                                      <li>• Potrai annullare in qualsiasi momento</li>
                                      <li>• Dopo 30 giorni l'accesso sarà disabilitato</li>
                                    </ul>
                                  </div>
                                </div>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel onClick={() => setCancelStep(0)}>Annulla</AlertDialogCancel>
                              <Button 
                                variant="destructive" 
                                onClick={() => setCancelStep(2)}
                                data-testid="btn-step1-continua"
                              >
                                Continua
                              </Button>
                            </AlertDialogFooter>
                          </>
                        )}
                        
                        {/* Step 2: Motivo e backup */}
                        {cancelStep === 2 && (
                          <>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Aiutaci a migliorare</AlertDialogTitle>
                              <AlertDialogDescription asChild>
                                <div className="space-y-4 text-left">
                                  <p>Perché stai cancellando l'account? (facoltativo ma utile)</p>
                                  
                                  <div className="space-y-2">
                                    <Label>Motivo principale</Label>
                                    <Select 
                                      value={motivoCancellazione} 
                                      onValueChange={setMotivoCancellazione}
                                    >
                                      <SelectTrigger data-testid="select-motivo">
                                        <SelectValue placeholder="Seleziona un motivo..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {MOTIVI_CANCELLAZIONE.map(m => (
                                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  
                                  {motivoCancellazione === 'altro' && (
                                    <div className="space-y-2">
                                      <Label>Dettagli (opzionale)</Label>
                                      <Textarea 
                                        value={motivoDettaglio}
                                        onChange={(e) => setMotivoDettaglio(e.target.value)}
                                        placeholder="Raccontaci di più..."
                                        rows={3}
                                        data-testid="textarea-motivo-dettaglio"
                                      />
                                    </div>
                                  )}
                                  
                                  <div className="bg-blue-50 p-3 rounded-lg">
                                    <div className="flex items-start gap-3">
                                      <Download className="w-5 h-5 text-blue-600 mt-0.5" />
                                      <div>
                                        <p className="font-medium text-blue-800">Hai scaricato il backup?</p>
                                        <p className="text-sm text-blue-700">
                                          Ti consigliamo di scaricare tutti i tuoi dati prima di procedere.
                                        </p>
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="p-0 h-auto text-blue-600"
                                          onClick={handleDownloadBackup}
                                        >
                                          Scarica backup ora
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel onClick={() => setCancelStep(0)}>Annulla</AlertDialogCancel>
                              <Button 
                                variant="outline"
                                onClick={() => setCancelStep(1)}
                              >
                                Indietro
                              </Button>
                              <Button 
                                variant="destructive" 
                                onClick={() => setCancelStep(3)}
                                data-testid="btn-step2-continua"
                              >
                                Continua
                              </Button>
                            </AlertDialogFooter>
                          </>
                        )}
                        
                        {/* Step 3: Conferma finale */}
                        {cancelStep === 3 && (
                          <>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-red-700">Conferma Cancellazione</AlertDialogTitle>
                              <AlertDialogDescription asChild>
                                <div className="space-y-4 text-left">
                                  <p>Ultimo passaggio prima di inviare la richiesta di cancellazione.</p>
                                  
                                  <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                                    <div className="flex items-start space-x-3">
                                      <Checkbox 
                                        id="conferma-backup"
                                        checked={confermaBackup}
                                        onCheckedChange={(checked) => setConfermaBackup(checked === true)}
                                        data-testid="checkbox-conferma-backup"
                                      />
                                      <label 
                                        htmlFor="conferma-backup" 
                                        className="text-sm font-medium text-red-800 cursor-pointer"
                                      >
                                        Confermo di aver scaricato il backup dei miei dati, oppure di non averne bisogno. 
                                        Capisco che dopo 30 giorni l'account sarà disattivato.
                                      </label>
                                    </div>
                                  </div>
                                  
                                  {!motivoCancellazione && (
                                    <p className="text-amber-600 text-sm flex items-center gap-2">
                                      <AlertTriangle className="w-4 h-4" />
                                      Non hai selezionato un motivo. Puoi tornare indietro per specificarlo.
                                    </p>
                                  )}
                                </div>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel onClick={() => setCancelStep(0)}>Annulla tutto</AlertDialogCancel>
                              <Button 
                                variant="outline"
                                onClick={() => setCancelStep(2)}
                              >
                                Indietro
                              </Button>
                              <Button 
                                variant="destructive" 
                                onClick={handleRichiediCancellazione}
                                disabled={!confermaBackup || submittingCancellazione}
                                data-testid="btn-conferma-cancellazione"
                              >
                                {submittingCancellazione ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Elaborazione...
                                  </>
                                ) : (
                                  <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Conferma Cancellazione
                                  </>
                                )}
                              </Button>
                            </AlertDialogFooter>
                          </>
                        )}
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="disclaimer-banner">
        <p>
          <strong>Nota:</strong> Le impostazioni di conservazione devono essere validate dall'ente in base alla normativa applicabile, 
          alle policy interne e alle indicazioni dei propri consulenti.
        </p>
      </div>
    </div>
  );
}
